import { createSelector } from 'reselect';
import { produce } from 'immer';
import { range, sortBy } from 'lodash-es';
import { v4 } from 'uuid';

import { ScopedType, ScopedAsyncType } from './scoped-type';
import { asyncAction } from './async-action';
import * as service from '../../services/starter-fertilizer-service';

const AsyncType = ScopedAsyncType('STARTER_FERTILIZER');
const Type = ScopedType('STARTER_FERTILIZER');

const SLICE = 'starterFertilizer';
const LOAD_FIELDS = AsyncType('LOAD_FIELDS');
const LOAD_PRESCRIPTION = AsyncType('LOAD_PRESCRIPTION');
const LOAD_PRESCRIPTIONS = AsyncType('LOAD_PRESCRIPTIONS');
const VALIDATE_FIELDS = Type('VALIDATE_FIELDS');
const VALIDATED_FIELDS = Type('VALIDATED_FIELDS');
const SELECT_FIELDS = Type('SELECT_FIELDS');
const FOCUS_FIELDS = Type('FOCUS_FIELDS');
const SELECT_PRESCRIPTIONS = Type('SELECT_PRESCRIPTIONS');
const FOCUS_PRESCRIPTIONS = Type('FOCUS_PRESCRIPTIONS');
const CANCEL_PRESCRIPTION = Type('CANCEL_PRESCRIPTION');
const UPDATE_PRESCRIPTION = Type('UPDATE_PRESCRIPTION');
const DELETE_PRESCRIPTIONS = AsyncType('DELETE_PRESCRIPTIONS');
const UPDATE_APPLIED = AsyncType('UPDATE_APPLIED');
const SELECT_YEAR = Type('SELECT_YEAR');

function addPrescriptionsTo(field, prescriptions) {
  return {
    ...field,
    prescriptions: prescriptions?.filter((prescription) =>
      prescription.fields.some((rxField) => rxField.aaid === field.aaid),
    ),
  };
}

function addPrescriptionInfo(rxField, year) {
  const appliedPrescriptionsForYear = rxField.prescriptions
    ?.filter((p) => p.cropYear === year)
    ?.filter((p) => p.isApplied === true);

  const latestApplied = sortBy(appliedPrescriptionsForYear, 'created').at(-1);
  const fieldStats = latestApplied?.fields?.find(
    ({ aaid }) => aaid === rxField.aaid,
  );

  return {
    ...rxField,
    averageGallons: fieldStats?.avgGallons,
    fulfilledPercentage: fieldStats?.fulfilledPercentage,
    underFulfilledPercentage: fieldStats?.underFulfilledPercentage,
    overFulfilledPercentage: fieldStats?.overFulfilledPercentage,
  };
}

function addContractInfo(rxField, growerFields) {
  const growerField = growerFields.find(
    ({ aaManagedId }) => aaManagedId === rxField.aaid,
  );

  const noContractError =
    growerField && growerField.isContractedFullAccess ? '' : 'No Contract';

  const validationError = [noContractError, rxField.validationError]
    .filter(Boolean)
    .join(', ');

  return {
    ...rxField,
    isContractedFullAccess: growerField?.isContractedFullAccess,
    validationError,
  };
}

export const selectors = {
  isWorking: (state) => state[SLICE].working,
  hasFailed: (state) => state[SLICE].error,
  draftPrescription: (state) => state[SLICE].draftPrescription,
  year: (state) => state[SLICE].year,
  availableYears: (state) => state[SLICE].availableYears,

  prescriptions: createSelector(
    (state) => state[SLICE].prescriptions,
    (state) => state[SLICE].year,
    (prescriptions, year) => prescriptions?.filter((p) => p.cropYear === year),
  ),

  fields: createSelector(
    (state) => state[SLICE].fields,
    (state) => state[SLICE].prescriptions,
    (state) => state[SLICE].year,
    (state) => state.fields.growerFields,
    (fields, prescriptions, year, growerFields) =>
      fields
        ?.map((field) => addPrescriptionsTo(field, prescriptions))
        ?.map((rxField) => addPrescriptionInfo(rxField, year))
        ?.map((rxField) => addContractInfo(rxField, growerFields)),
  ),

  selectedProducts: createSelector(
    (state) => state[SLICE].draftPrescription,
    (draft) => {
      if (!draft?.products) return [];
      return draft.products
        .filter((product) => product.selected)
        .map((product) =>
          ['nitrogen', 'phosphorus', 'potassium', 'sulfur'].reduce(
            (memo, element) => ({
              ...memo,
              [`${element}Units`]:
                product.density *
                (product[element] / 100) *
                ((product.percentage || 0) / 100),
              [`${element}Percentage`]:
                product[element] * ((product.percentage || 0) / 100),
            }),
            product,
          ),
        );
    },
  ),

  selectedFields: createSelector(
    (state) => state[SLICE].fields,
    (state) => state[SLICE].prescriptions,
    (state) => state.fields.growerFields,
    (fields, prescriptions, growerFields) =>
      (fields || [])
        .filter((f) => f.isSelected)
        .filter((f) => f.isValidating || f.isValid)
        .map((field) => addPrescriptionsTo(field, prescriptions))
        .map((rxField) => addContractInfo(rxField, growerFields)),
  ),

  focusedFields: createSelector(
    (state) => state[SLICE].fields,
    (state) => state[SLICE].prescriptions,
    (state) => state[SLICE].year,
    (fields, prescriptions, year) =>
      (fields || [])
        .filter((field) => field.isFocused)
        .map((field) =>
          addPrescriptionsTo(
            field,
            prescriptions?.filter((p) => p.cropYear === year),
          ),
        ),
  ),

  selectedPrescriptions: createSelector(
    (state) => state[SLICE].prescriptions,
    (prescriptions) => (prescriptions || []).filter((p) => p.isSelected),
  ),

  focusedPrescriptions: createSelector(
    (state) => state[SLICE].prescriptions,
    (prescriptions) => prescriptions?.filter((p) => p.isFocused) || [],
  ),
};

export const actions = {
  cancelPrescription,
  loadPrescription,
  loadPrescriptions,
  loadFields,
  setFocusedFields,
  setSelectedFields,
  setFocusedPrescriptions,
  setSelectedPrescriptions,
  updateApplied,
  updatePrescription,
  deletePrescriptions,
  setSelectedYear,
};

const reducerHandlers = {
  [LOAD_FIELDS.start]: reduceLoadFieldsStart,
  [LOAD_FIELDS.complete]: reduceLoadFieldsComplete,
  [LOAD_PRESCRIPTION.start]: reduceLoadPrescriptionStart,
  [LOAD_PRESCRIPTION.complete]: reduceLoadPrescriptionComplete,
  [LOAD_PRESCRIPTION.failure]: reduceLoadPrescriptionFailure,
  [LOAD_PRESCRIPTIONS.start]: reduceLoadPrescriptionsStart,
  [LOAD_PRESCRIPTIONS.complete]: reduceLoadPrescriptionsComplete,
  [DELETE_PRESCRIPTIONS.complete]: reduceDeletePrescriptionsComplete,
  [VALIDATE_FIELDS]: reduceValidateFields,
  [VALIDATED_FIELDS]: reduceValidatedFields,
  [SELECT_FIELDS]: reduceSelectFields,
  [FOCUS_FIELDS]: reduceFocusFields,
  [SELECT_PRESCRIPTIONS]: reduceSelectPrescriptions,
  [FOCUS_PRESCRIPTIONS]: reduceFocusPrescriptions,
  [CANCEL_PRESCRIPTION]: reduceCancelPrescription,
  [UPDATE_PRESCRIPTION]: reduceUpdatePrescription,
  [UPDATE_APPLIED.start]: reduceUpdateAppliedStart,
  [UPDATE_APPLIED.failure]: reduceUpdateAppliedFailure,
  [SELECT_YEAR]: reduceSelectYear,
};

function cancelPrescription() {
  return { type: CANCEL_PRESCRIPTION };
}

function loadPrescription(growerId, id) {
  const requestId = v4();

  return asyncAction(
    LOAD_PRESCRIPTION,
    (_, getState) =>
      service.getPrescription(growerId, id).then((prescription) => {
        const { requestId: currentRequestId } = getState()[SLICE];
        return requestId === currentRequestId ? prescription : {};
      }),
    { startPayload: { requestId }, failurePayload: { id } },
  );
}

function loadPrescriptions(growerId) {
  return asyncAction(LOAD_PRESCRIPTIONS, (_, getState) =>
    service.getPrescriptions(growerId).then((prescriptions) => {
      const { growerFields } = getState().fields;

      return prescriptions.map((prescription) => {
        const areFieldsContracted = prescription.fields.every(
          (field) =>
            addContractInfo(field, growerFields).isContractedFullAccess,
        );

        return {
          ...prescription,
          hasUncontractedFields: !areFieldsContracted,
        };
      });
    }),
  );
}

function loadFields(growerId) {
  return asyncAction(LOAD_FIELDS, () => service.getFields(growerId));
}

function setSelectedFields(ids) {
  return async (dispatch, getState) => {
    dispatch({ type: SELECT_FIELDS, payload: ids });

    const allFields = getState()[SLICE].fields || [];
    const valid = allFields.filter((f) => f.isValid || f.isValidating);
    const validateIds = ids.filter((id) => !valid.find((v) => v.id === id));
    const fields = validateIds.map((id) => allFields.find((f) => f.id === id));

    if (!fields.length) return;

    dispatch({ type: VALIDATE_FIELDS, payload: validateIds });
    const results = await service.validateFields(fields.map((f) => f.aaid));
    dispatch({ type: VALIDATED_FIELDS, payload: results });
  };
}

function setSelectedPrescriptions(ids) {
  return { type: SELECT_PRESCRIPTIONS, payload: ids };
}

function setFocusedFields(ids) {
  return { type: FOCUS_FIELDS, payload: ids };
}

function setFocusedPrescriptions(ids) {
  return { type: FOCUS_PRESCRIPTIONS, payload: ids };
}

function updateApplied(growerId, id, isApplied) {
  return asyncAction(
    UPDATE_APPLIED,
    async (dispatch) =>
      service
        .updateApplied(growerId, id, isApplied)
        .then(() => dispatch(loadFields(growerId))),
    { startPayload: { id, isApplied }, failurePayload: { id, isApplied } },
  );
}

function updatePrescription(changes) {
  return { type: UPDATE_PRESCRIPTION, payload: changes };
}

function deletePrescriptions(growerId, prescriptions) {
  return asyncAction(DELETE_PRESCRIPTIONS, async () =>
    service
      .deletePrescriptions(growerId, prescriptions)
      .then(() => prescriptions.map(({ id }) => id)),
  );
}

function setSelectedYear(year) {
  return { type: SELECT_YEAR, payload: year };
}

function reduceLoadFieldsComplete(state, payload) {
  state.fields = payload;
}

function reduceLoadFieldsStart(state) {
  state.fields = null;
}

function reduceLoadPrescriptionsComplete(state, payload) {
  state.prescriptions = payload;
}

function reduceLoadPrescriptionStart(state, payload) {
  Object.assign(state, payload);
}

function reduceLoadPrescriptionComplete(state, payload) {
  if (!payload.id) return;
  const oldOne = state.prescriptions.find((rx) => rx.id === payload.id);

  if (oldOne) {
    Object.assign(oldOne, payload);
  } else {
    state.prescriptions.push(payload);
  }
}

function reduceLoadPrescriptionFailure(state, payload) {
  if (payload.error?.response?.status === 404) {
    state.prescriptions = state.prescriptions.filter(
      (p) => p.id.toUpperCase() !== payload.id.toUpperCase(),
    );
  }
}

function reduceLoadPrescriptionsStart(state) {
  state.prescriptions = null;
}

function reduceDeletePrescriptionsComplete(state, ids) {
  state.prescriptions = state.prescriptions.filter((p) => !ids.includes(p.id));
}

function reduceFocusFields(state, ids) {
  state.fields.forEach((field) => {
    if (field.isFocused && ids.includes(field.id)) {
      field.isFocused = false;
    } else {
      field.isFocused = ids.includes(field.id);
    }
  });
}

function reduceFocusPrescriptions(state, ids) {
  state.prescriptions.forEach((prescription) => {
    if (prescription.isFocused && ids.includes(prescription.id)) {
      prescription.isFocused = false;
    } else {
      prescription.isFocused = ids.includes(prescription.id);
    }
  });
}

function reduceSelectFields(state, selectedIds) {
  state.fields.forEach((field) => {
    field.isSelected = selectedIds.includes(field.id);
  });
}

function reduceSelectPrescriptions(state, selectedIds) {
  state.prescriptions.forEach((prescription) => {
    prescription.isSelected = selectedIds.includes(prescription.id);
  });
}

function reduceValidateFields(state, ids) {
  state.fields.forEach((field) => {
    if (ids.includes(field.id)) {
      delete field.isValid;
      delete field.validationError;
      field.isValidating = true;
    }
  });
}

function reduceValidatedFields(state, results) {
  results.forEach(({ aaid, isValid, reason }) => {
    const field = state.fields.find((f) => aaid === f.aaid);
    delete field.isValidating;
    field.isValid = isValid;
    if (!isValid) field.validationError = `Missing Information: ${reason}`;
  });
}

function reduceCancelPrescription(state) {
  delete state.draftPrescription;
}

function reduceUpdatePrescription(state, changes) {
  Object.assign(state.draftPrescription, changes);
}

function reduceUpdateAppliedStart(state, { id, isApplied }) {
  state.prescriptions.forEach((prescription) => {
    if (prescription.id === id) prescription.isApplied = isApplied;
  });
}

function reduceUpdateAppliedFailure(state, { id, isApplied }) {
  state.prescriptions.forEach((prescription) => {
    if (prescription.id === id) prescription.isApplied = !isApplied;
  });
}

function reduceSelectYear(state, selectedYear) {
  state.year = selectedYear;
}

export const reducers = {
  [SLICE]: (
    state = {
      year: new Date().getFullYear(),
      availableYears: range(2023, new Date().getFullYear() + 2),
    },
    { type, payload } = {},
  ) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};
